import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { useToast } from '~/lib/toasts';

interface UseGeolocationOptions {
  onSuccess?: (position: GeolocationPosition) => void;
  onError?: (error: GeolocationPositionError) => void;
}

/**
 * Hook for handling geolocation functionality
 * Abstracts the logic for getting the user's current location
 */
export const useGeolocation = () => {
  const t = useTranslations('LocationSearch');
  const [isLoading, setIsLoading] = useState(false);
  const [location, setLocation] = useState<GeolocationCoordinates | null>(null);
  const [error, setError] = useState<GeolocationPositionError | null>(null);
  const { showToast } = useToast();
  /**
   * Get the user's current location
   * @param options Optional configuration
   */
  const getCurrentLocation = (options?: UseGeolocationOptions) => {
    const { onSuccess, onError } = options || {};
    setIsLoading(true);
    setError(null);
    
    // Check if geolocation is supported
    if (!navigator.geolocation) {
      const unsupportedError = {
        code: 0,
        message: 'Geolocation is not supported',
        PERMISSION_DENIED: 1,
        POSITION_UNAVAILABLE: 2,
        TIMEOUT: 3
      } as GeolocationPositionError;
      
      setError(unsupportedError);
      
        showToast({
          title: t('locationNotSupported'),
          description: t('locationNotSupportedDescription'),
        }, { status: 'error' });
    
      
      if (onError) {
        onError(unsupportedError);
      }
      
      setIsLoading(false);
      return;
    }

    // Try with less strict options first to improve success rate
    const getLocationWithOptions = (options: PositionOptions) => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          // Success handler
          setLocation(position.coords);
          
            showToast({
              title: t('locationFound'),
              description: t('locationFoundDescription'),
            }, { status: 'success' });
          
          
          if (onSuccess) {
            onSuccess(position);
          }
          
          setIsLoading(false);
        },
        (error) => {
          // Error handler
          console.error('Error getting location:', error, options);
          setError(error);
          
          // If position is unavailable and we were using high accuracy, try again with lower accuracy
          if (error.code === error.POSITION_UNAVAILABLE && options.enableHighAccuracy) {
            console.log('Retrying with lower accuracy...');
            // Try again with lower accuracy
            getLocationWithOptions({
              ...options,
              enableHighAccuracy: false,
              maximumAge: 300000, // Accept positions up to 5 minutes old
            });
            return;
          }
          
            let errorMessage = t('locationError');
            switch (error.code) {
              case error.PERMISSION_DENIED:
                errorMessage = t('locationPermissionDenied');
                break;
              case error.POSITION_UNAVAILABLE:
                errorMessage = t('locationUnavailable');
                break;
              case error.TIMEOUT:
                errorMessage = t('locationTimeout');
                break;
            }
            
            showToast({
              title: t('locationError'),
              description: errorMessage
            }, { status: 'error' });
          
          
          if (onError) {
            onError(error);
          }
          
          setIsLoading(false);
        },
        options
      );
    };
    
    // Start with moderate accuracy settings
    getLocationWithOptions({
      enableHighAccuracy: true,  // Try high accuracy first
      timeout: 10000,           // 10 second timeout
      maximumAge: 60000         // Accept positions up to 1 minute old
    });
  };

  return {
    isLoading,
    location,
    error,
    getCurrentLocation
  };
};
